import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../../apiClient';

const RefundSection = ({ tenantId, tenant }) => {
    const [latestPayment, setLatestPayment] = useState(null);
    const [totalRefunded, setTotalRefunded] = useState(0);
    const [refundHistory, setRefundHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedRefund, setSelectedRefund] = useState(null);

    // Form states
    const [invoiceDetail, setInvoiceDetail] = useState('');
    const [outageStart, setOutageStart] = useState('');
    const [outageEnd, setOutageEnd] = useState('');
    const [compensationDays, setCompensationDays] = useState(0);
    const [calculationType, setCalculationType] = useState('auto');
    const [selectedPlanKey, setSelectedPlanKey] = useState(tenant?.plan || 'free');
    const [manualRefundAmountEx, setManualRefundAmountEx] = useState(0);
    const [reason, setReason] = useState('システム障害');
    const [memo, setMemo] = useState('');
    const [refundMode, setRefundMode] = useState('credit'); // 'refund' (即時) or 'credit' (次回)
    const [executing, setExecuting] = useState(false);

    const planPrices = {
        trial: 0,
        free: 0,
        lite: 1000,
        standard: 3000,
        business: 8000
    };
    const monthlyFeeEx = planPrices[selectedPlanKey] || 0;
    const taxRate = 0.1;

    // 手動入力が選ばれたら即時返金モードに切り替える
    useEffect(() => {
        if (calculationType === 'manual') {
            setRefundMode('refund');
        } else {
            setRefundMode('credit');
        }
    }, [calculationType]);

    const fetchRefundData = async () => {
        try {
            setLoading(true);
            const [payRes, histRes] = await Promise.all([
                apiClient.get(`/v1/admin/tenants/${tenantId}/latest-payment`),
                apiClient.get(`/v1/admin/tenants/${tenantId}/refunds`)
            ]);
            if (payRes.ok) {
                const data = await payRes.json();
                setLatestPayment(data.latestPayment);
                setTotalRefunded(data.totalRefunded);
            }
            if (histRes.ok) {
                setRefundHistory(await histRes.json());
            }
        } catch (error) {
            console.error("Error fetching refund data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRefundData();
    }, [tenantId]);

    useEffect(() => {
        if (outageStart && outageEnd) {
            const start = new Date(outageStart);
            const end = new Date(outageEnd);
            const diffMs = end - start;
            if (diffMs > 0) {
                const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                setCompensationDays(days);
            } else {
                setCompensationDays(0);
            }
        }
    }, [outageStart, outageEnd]);

    const calculateRefundAmounts = () => {
        let amountEx = 0;
        if (calculationType === 'auto') {
            amountEx = Math.floor((monthlyFeeEx / 30) * compensationDays);
        } else {
            amountEx = parseInt(manualRefundAmountEx) || 0;
        }
        const tax = Math.floor(amountEx * taxRate);
        const total = amountEx + tax;
        return { amountEx, tax, total };
    };

    const { amountEx, tax, total } = calculateRefundAmounts();

    const formatDateJst = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const jst = new Date(d.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
        const y = jst.getFullYear();
        const m = String(jst.getMonth() + 1).padStart(2, '0');
        const day = String(jst.getDate()).padStart(2, '0');
        const h = String(jst.getHours()).padStart(2, '0');
        const min = String(jst.getMinutes()).padStart(2, '0');
        return `${y}/${m}/${day} ${h}:${min}`;
    };

    const handleExecuteRefund = async () => {
        if (!invoiceDetail) {
            alert('請求書明細を入力してください。');
            return;
        }
        
        if (calculationType === 'auto' && compensationDays < 1) {
            alert('自動計算の場合、補償日数は1日以上必要です。障害期間を確認してください。');
            return;
        }

        if (calculationType === 'manual' && amountEx <= 0) {
            alert('返金額を入力してください。');
            return;
        }

        // 即時返金（Stripe）の場合のチェック
        if (refundMode === 'refund') {
            if (!latestPayment) {
                alert('即時返金を実行するための決済履歴が見つかりません。');
                return;
            }
            if (total + totalRefunded > latestPayment.amount) {
                alert('累計返金額が最新決済金額を超えています。');
                return;
            }
        }

        const confirmMsg = refundMode === 'refund' 
            ? `税込${total.toLocaleString()}円を直ちにStripe経由で返金します。よろしいですか？`
            : `税込${total.toLocaleString()}円を次回請求時の精算分として登録します。よろしいですか？`;

        if (!window.confirm(confirmMsg)) return;

        setExecuting(true);
        try {
            const response = await apiClient.post(`/v1/admin/tenants/${tenantId}/refunds`, {
                invoiceDetail,
                outageStartAt: outageStart || null,
                outageEndAt: outageEnd || null,
                compensationDays,
                calculationType,
                monthlyFeeExcludingTax: monthlyFeeEx,
                refundAmountExcludingTax: amountEx,
                taxRate,
                taxAmount: tax,
                refundAmountIncludingTax: total,
                reason,
                memo,
                paymentIntentId: latestPayment?.id,
                mode: refundMode
            });

            if (response.ok) {
                alert('返金処理が完了しました。');
                setShowRefundModal(false);
                fetchRefundData();
            } else {
                const err = await response.json();
                alert(`エラー: ${err.error}`);
            }
        } catch (error) {
            console.error("Error executing refund:", error);
            alert('処理に失敗しました。');
        } finally {
            setExecuting(false);
        }
    };

    const openDetail = (refund) => {
        setSelectedRefund(refund);
        setShowDetailModal(true);
    };

    const planDisplay = {
        trial: 'トライアル',
        free: 'フリー',
        lite: 'ライト',
        standard: 'スタンダード',
        business: 'ビジネス'
    };

    if (loading) return <div className="mt-8 text-center">返金情報読み込み中...</div>;

    return (
        <div className="mt-8 bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
                <h3 className="text-lg font-semibold">返金</h3>
                <button 
                    onClick={() => setShowRefundModal(true)}
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm font-bold shadow-sm"
                >
                    返金を実行する
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="p-3 border rounded bg-gray-50">
                    <dt className="text-xs font-medium text-gray-500 uppercase">Stripe Customer ID</dt>
                    <dd className="text-sm font-mono">{tenant.stripe_account_id || '-'}</dd>
                </div>
                <div className="p-3 border rounded bg-gray-50">
                    <dt className="text-xs font-medium text-gray-500 uppercase">契約プラン</dt>
                    <dd className="text-sm font-bold">{planDisplay[tenant.plan] || tenant.plan}</dd>
                </div>
                <div className="p-3 border rounded bg-gray-50">
                    <dt className="text-xs font-medium text-gray-500 uppercase">最新決済日</dt>
                    <dd className="text-sm">
                        {latestPayment ? new Date(latestPayment.created * 1000).toLocaleDateString() : '-'}
                    </dd>
                </div>
                <div className="p-3 border rounded bg-gray-50">
                    <dt className="text-xs font-medium text-gray-500 uppercase">累計返金額</dt>
                    <dd className="text-sm text-red-600 font-bold">{totalRefunded.toLocaleString()}円</dd>
                </div>
            </div>

            <h4 className="text-md font-medium mb-2">返金・精算履歴</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">実施日時</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">明細（期間）</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">返金額（税込）</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">方式</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">理由</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ステータス</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">詳細</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {refundHistory.map((refund) => (
                            <tr key={refund.id}>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                    {new Date(refund.executedAt).toLocaleString()}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                    {refund.invoiceDetail}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 font-bold">
                                    {refund.refundAmountIncludingTax.toLocaleString()}円
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                    {refund.mode === 'refund' ? 'Stripe返金' : '次回請求減額'}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                    {refund.reason}
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                        refund.status === 'succeeded' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                                    }`}>
                                        {refund.status}
                                    </span>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm">
                                    <button onClick={() => openDetail(refund)} className="text-indigo-600 hover:text-indigo-900">詳細</button>
                                </td>
                            </tr>
                        ))}
                        {refundHistory.length === 0 && (
                            <tr>
                                <td colSpan="7" className="px-4 py-4 text-center text-sm text-gray-500">履歴はありません。</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Refund Modal */}
            {showRefundModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
                        <h3 className="text-lg font-bold mb-4 border-b pb-2">個別返金実行</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">返金タイミング（必須）</label>
                                <div className="mt-2 flex gap-4">
                                    <label className="flex items-center">
                                        <input type="radio" name="mode" value="credit" checked={refundMode === 'credit'} onChange={(e) => setRefundMode(e.target.value)} className="mr-2" />
                                        <span className="text-sm">次回請求時に減額（推奨）</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="radio" name="mode" value="refund" checked={refundMode === 'refund'} onChange={(e) => setRefundMode(e.target.value)} className="mr-2" />
                                        <span className="text-sm">今すぐStripeで返金</span>
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">※手動入力の場合は「今すぐStripeで返金」が自動選択されます。決済履歴がない場合は実行できません。</p>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">請求書明細（必須）</label>
                                <input 
                                    type="text" 
                                    className="mt-1 block w-full border rounded-md shadow-sm p-2"
                                    value={invoiceDetail}
                                    onChange={(e) => setInvoiceDetail(e.target.value)}
                                    placeholder="例: システム障害に伴う補償"
                                />
                                {outageStart && outageEnd && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        ※保存時の実際の明細名: <span className="font-semibold">{invoiceDetail || 'システム障害に伴う補償'} ({formatDateJst(outageStart)}～{formatDateJst(outageEnd)})</span>
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">障害開始日時</label>
                                <input 
                                    type="datetime-local" 
                                    className="mt-1 block w-full border rounded-md shadow-sm p-2"
                                    value={outageStart}
                                    onChange={(e) => setOutageStart(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">障害終了日時</label>
                                <input 
                                    type="datetime-local" 
                                    className="mt-1 block w-full border rounded-md shadow-sm p-2"
                                    value={outageEnd}
                                    onChange={(e) => setOutageEnd(e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">返金方法</label>
                                <select 
                                    className="mt-1 block w-full border rounded-md shadow-sm p-2"
                                    value={calculationType}
                                    onChange={(e) => setCalculationType(e.target.value)}
                                >
                                    <option value="auto">自動計算</option>
                                    <option value="manual">手動入力</option>
                                </select>
                            </div>

                            {calculationType === 'auto' ? (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">計算基準プラン</label>
                                    <select 
                                        className="mt-1 block w-full border rounded-md shadow-sm p-2"
                                        value={selectedPlanKey}
                                        onChange={(e) => setSelectedPlanKey(e.target.value)}
                                    >
                                        <option value="lite">ライト (1,000円)</option>
                                        <option value="standard">スタンダード (3,000円)</option>
                                        <option value="business">ビジネス (8,000円)</option>
                                        <option value="trial">トライアル (0円)</option>
                                        <option value="free">フリー (0円)</option>
                                    </select>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">返金額（税別）</label>
                                    <input 
                                        type="number" 
                                        className="mt-1 block w-full border rounded-md shadow-sm p-2"
                                        value={manualRefundAmountEx}
                                        onChange={(e) => setManualRefundAmountEx(e.target.value)}
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700">補償日数</label>
                                <input 
                                    type="number" 
                                    className="mt-1 block w-full border rounded-md shadow-sm p-2"
                                    value={compensationDays}
                                    onChange={(e) => setCompensationDays(parseInt(e.target.value) || 0)}
                                    disabled={calculationType === 'manual'}
                                />
                                <p className="text-xs text-gray-500 mt-1">24時間ごとに1日加算</p>
                            </div>

                            <div className="md:col-span-2 bg-gray-50 p-4 rounded-md grid grid-cols-2 gap-4">
                                <div>
                                    <span className="text-sm text-gray-500">計算基準月額 (税別):</span>
                                    <span className="ml-2 font-semibold">{monthlyFeeEx.toLocaleString()}円</span>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">最新決済金額 (税込):</span>
                                    <span className="ml-2 font-semibold">{latestPayment?.amount.toLocaleString() || 0}円</span>
                                </div>
                                
                                <div>
                                    <span className="text-sm text-gray-500">返金額 (税別):</span>
                                    <span className="ml-2 font-bold text-lg">{amountEx.toLocaleString()}円</span>
                                </div>
                                <div>
                                    <span className="text-sm text-gray-500">消費税:</span>
                                    <span className="ml-2 font-semibold">{tax.toLocaleString()}円</span>
                                </div>
                                <div className="col-span-2 border-t pt-2 mt-2">
                                    <span className="text-md font-bold text-red-600">返金総額 (税込):</span>
                                    <span className="ml-2 text-xl font-bold text-red-600">{total.toLocaleString()}円</span>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">返金理由</label>
                                <select 
                                    className="mt-1 block w-full border rounded-md shadow-sm p-2"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                >
                                    <option value="システム障害">システム障害</option>
                                    <option value="サポート対応">サポート対応</option>
                                    <option value="その他">その他</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">管理メモ</label>
                                <textarea 
                                    className="mt-1 block w-full border rounded-md shadow-sm p-2"
                                    rows="2"
                                    value={memo}
                                    onChange={(e) => setMemo(e.target.value)}
                                    placeholder="管理者向けメモ（非公開）"
                                ></textarea>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3 border-t pt-4">
                            <button onClick={() => setShowRefundModal(false)} className="px-4 py-2 bg-gray-200 rounded">キャンセル</button>
                            <button 
                                onClick={handleExecuteRefund}
                                disabled={executing}
                                className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 font-bold"
                            >
                                {executing ? '処理中...' : '実行する'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail Modal */}
            {showDetailModal && selectedRefund && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
                        <h3 className="text-lg font-bold mb-4 border-b pb-2">返金詳細</h3>
                        <dl className="grid grid-cols-1 gap-y-2">
                            <div className="flex justify-between border-b pb-1">
                                <dt className="text-sm text-gray-500">実施日時</dt>
                                <dd className="text-sm font-medium">{new Date(selectedRefund.executedAt).toLocaleString()}</dd>
                            </div>
                            <div className="flex justify-between border-b pb-1">
                                <dt className="text-sm text-gray-500">返金方式</dt>
                                <dd className="text-sm font-bold text-indigo-600">{selectedRefund.mode === 'refund' ? 'Stripe即時返金' : '次回請求時に減額'}</dd>
                            </div>
                            <div className="flex justify-between border-b pb-1">
                                <dt className="text-sm text-gray-500">請求書明細</dt>
                                <dd className="text-sm font-medium">{selectedRefund.invoiceDetail}</dd>
                            </div>
                            {selectedRefund.outageStartAt && selectedRefund.outageEndAt && (
                                <div className="flex justify-between border-b pb-1">
                                    <dt className="text-sm text-gray-500">障害発生期間</dt>
                                    <dd className="text-sm font-medium">{formatDateJst(selectedRefund.outageStartAt)} ～ {formatDateJst(selectedRefund.outageEndAt)}</dd>
                                </div>
                            )}
                            <div className="flex justify-between border-b pb-1">
                                <dt className="text-sm text-gray-500">補償日数</dt>
                                <dd className="text-sm font-medium">{selectedRefund.compensationDays}日</dd>
                            </div>
                            <div className="flex justify-between border-b pb-1">
                                <dt className="text-sm text-gray-500">基準単価（税別）</dt>
                                <dd className="text-sm font-medium">{selectedRefund.monthlyFeeExcludingTax.toLocaleString()}円</dd>
                            </div>
                            <div className="flex justify-between border-b pb-1">
                                <dt className="text-sm text-gray-500 font-bold">返金額（税込）</dt>
                                <dd className="text-sm font-bold text-red-600">{selectedRefund.refundAmountIncludingTax.toLocaleString()}円</dd>
                            </div>
                            <div className="flex justify-between border-b pb-1">
                                <dt className="text-sm text-gray-500">返金理由</dt>
                                <dd className="text-sm font-medium">{selectedRefund.reason}</dd>
                            </div>
                            <div className="flex justify-between border-b pb-1">
                                <dt className="text-sm text-gray-500">ステータス</dt>
                                <dd className="text-sm font-medium">{selectedRefund.status}</dd>
                            </div>
                            <div className="mt-2">
                                <dt className="text-sm text-gray-500">管理メモ</dt>
                                <dd className="text-sm bg-gray-50 p-2 rounded mt-1">{selectedRefund.memo || 'なし'}</dd>
                            </div>
                        </dl>
                        <div className="mt-6 flex justify-end">
                            <button onClick={() => setShowDetailModal(false)} className="px-4 py-2 bg-gray-200 rounded">閉じる</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RefundSection;
